import React, { useEffect, useState } from 'react';

import { useLocation } from '@reach/router';
import classnames from 'classnames';

import { MOBILE_OPEN, SIDEBAR_LANGUAGE, SOFTWARE_IDE_PAGE, SOFTWARE_PAGE, SOFTWARE_PLC_PAGE } from '../../utils/constants';
import { IconArrowLeft, IconSemiArrowLeft, IconSemiArrowRight, getIcon } from '../Icons';

import LanguageLocalization from '../LanguageLocalization/LanguageLocalization';
import { TooltipTrigger } from '../TooltipTrigger/TooltipTrigger';
import './SideBar.scss';
import useWindowSize from '../../hooks/useWindowSize';

interface NavLink {
  id: number;
  name: string;
  url: string;
  icon: string;
}

interface PrevPageProps {
  title: string;
  pathname: string;
}

export interface SideBarProps {
  links: NavLink[];
  prevPage?: PrevPageProps;
  selectedItem?: string;
  actualPage?: string;
  leafPage?: string;
  nestedCategories?: any;
  isTutorials?: boolean;
}

export default function TutorialSideBar(props: SideBarProps): React.ReactElement {
  const { prevPage, actualPage, nestedCategories, leafPage, selectedItem, isTutorials } = props;
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isDesktop = typeof window !== 'undefined' ? window.innerWidth > 768 : true;
  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 580 : false;
  const isTablet = typeof window !== 'undefined' ? window.innerWidth > 580 && window.innerWidth <= 768 : false;
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const { width } = useWindowSize();

  function toggleSidebar(setIsCollapsed) {
    setIsCollapsed(!isCollapsed);
    if (document.querySelector('.sidebar') && document.querySelector('.sidebar__control--icon') && document.querySelector('.sidebar__first-level')) {
      document.querySelector('.sidebar').classList.toggle('closed');
      document.querySelector('.sidebar__control--icon').classList.toggle('closed');
      document.querySelector('.sidebar__first-level').classList.toggle('hideOverflow');
    }
  }

  useEffect(() => {
    if (isTutorials) toggleSidebar(setIsCollapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMobile || isTablet) {
      const checkUserData = () => {
        const item = localStorage.getItem(MOBILE_OPEN);

        if (item) {
          setIsMobileOpen(item === 'opened' ? true : false);
        }
      };

      const element = window;

      element.addEventListener('click', checkUserData);

      return () => {
        element.removeEventListener('click', checkUserData);
      };
    }
  }, [isMobile, isTablet]);

  useEffect(() => {
    const item = localStorage.getItem(MOBILE_OPEN);
    if (document.querySelector('.sidebar') && isMobile) {
      if (item === 'opened') {
        (document.querySelector('.sidebar') as HTMLElement).style.display = 'unset';
      } else {
        (document.querySelector('.sidebar') as HTMLElement).style.display = 'none';
      }
    }
  }, [isMobile, isMobileOpen]);

  const Item = ({ navEl, opened }: any) => {
    const [open, setIsOpened] = useState(opened);

    useEffect(() => {
      if (open) {
        document.getElementsByClassName('sidebar__main-category--opened')[0].classList.toggle('accordion');
      } else if (
        !open &&
        document.getElementsByClassName('accordion')[0] &&
        !document.getElementsByClassName('accordion')[0].classList.contains('sidebar__main-category--opened')
      ) {
        document.getElementsByClassName('accordion')[0]?.classList.remove('accordion');
      }
    }, [open]);

    return (
      <button
        key={navEl.firstLevelCategory}
        className="sidebar__categories"
        onClick={(e) => {
          if ((e.target as HTMLElement).tagName !== 'A') setIsOpened(!open);
          e.currentTarget.classList.toggle('accordion');
        }}
      >
        <div className={classnames('sidebar__main-category', { 'sidebar__main-category--opened': open })}>
          <div className="sidebar__container">
            <h5>{navEl.firstLevelCategory}</h5>
          </div>
          {/* <div className="sidebar__main-category--icon">{opened ? <IconSemiArrowUp /> : <IconSemiArrowDown />}</div> */}
          <div className="sidebar__main-category--icon">
            <IconSemiArrowRight />
          </div>
        </div>
        {open &&
          navEl.nestedCategories?.map((categoryType) => {
            return (
              <div className="sidebar__category-type" key={categoryType.category} style={{ display: open ? 'static' : 'none' }}>
                {categoryType.category && (
                  <div className="sidebar__category-type--label">
                    <h6>{categoryType.category}</h6>
                    <span />
                  </div>
                )}
                <div className="sidebar__links">
                  {categoryType.nestedChildren.map((product) => {
                    if (product === null) return null;
                    return (
                      <a
                        href={product?.url}
                        key={product?.title}
                        className={classnames('sidebar__links--item', {
                          'sidebar__links--selected': product?.title === selectedItem || product?.title === leafPage,
                        })}
                      >
                        {product?.title}
                      </a>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </button>
    );
  };

  return (
    <>
      {isDesktop || !width || width >= 768 ? (
        <>
          <div className="sidebar">
            {location.pathname !== '/' && (
              <div className="sidebar__breadcrumbs">
                <a href={prevPage ? prevPage.pathname : '/'}>
                  <IconArrowLeft />
                  <span className="sidebar__breadcrumbs--space">{prevPage ? prevPage.title : 'All Docs'}</span>
                </a>
                <div className="sidebar__box">
                  {actualPage && <div className="sidebar__breadcrumbs--title">{actualPage}</div>}
                  {actualPage === SIDEBAR_LANGUAGE && <LanguageLocalization />}
                </div>
                <TooltipTrigger
                  TriggerElement={
                    <button className="sidebar__control" onClick={() => toggleSidebar(setIsCollapsed)}>
                      <IconSemiArrowLeft className="sidebar__control--icon" />
                    </button>
                  }
                  delay={0}
                  tooltipPlacement="right"
                  tooltipStrategy="absolute"
                  tooltipType="tooltip"
                  onClick={() => toggleSidebar(setIsCollapsed)}
                >
                  {isCollapsed ? 'Expand' : 'Collapse'}
                </TooltipTrigger>
              </div>
            )}
            <div className="sidebar__first-level">
              {/* {getNavigation()} */}
              <div className="sidebar__navigation">
                {nestedCategories?.map((navEl: any, i) => {
                  let opened = false;
                  // eslint-disable-next-line react/prop-types
                  navEl.nestedCategories?.forEach((categoryType) => {
                    categoryType.nestedChildren.forEach((product) => {
                      if (product?.title === actualPage || product?.title === selectedItem || product?.title === leafPage) {
                        opened = true;
                        return;
                      }
                    });
                  });
                  return <Item navEl={navEl} key={i} opened={opened} />;
                })}
              </div>
              {(location.pathname === SOFTWARE_PAGE || location.pathname === SOFTWARE_IDE_PAGE || location.pathname === SOFTWARE_PLC_PAGE) && (
                <a href="https://arduino.github.io/arduino-cli/0.34/" className="sidebar__programming">
                  <span>CLI</span>
                  {getIcon('IconArrowRightJet')}
                </a>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {isMobileOpen && (isMobile || isTablet || (width && width < 768)) ? (
            <div className="sidebar-mobile">
              {location.pathname !== '/' && (
                <div className="sidebar-mobile__breadcrumbs">
                  <a href={prevPage ? prevPage.pathname : '/'}>
                    <IconArrowLeft />
                    <span className="sidebar-mobile__breadcrumbs--space">{prevPage ? prevPage.title : 'All Docs'}</span>
                  </a>
                  {actualPage && <div className="sidebar-mobile__breadcrumbs--title">{actualPage}</div>}
                </div>
              )}
              <div className="sidebar-mobile__first-level">
                {/* {getNavigation()} */}
                <div className="sidebar-mobile__navigation">
                  {nestedCategories?.map((navEl: any, i) => {
                    let opened = false;
                    // eslint-disable-next-line react/prop-types
                    navEl.nestedCategories?.forEach((categoryType) => {
                      categoryType.nestedChildren.forEach((product) => {
                        if (product?.title === actualPage || product?.title === selectedItem || product?.title === leafPage) {
                          opened = true;
                          return;
                        }
                      });
                    });
                    return <Item navEl={navEl} key={i} opened={opened} />;
                  })}
                </div>
                {(location.pathname === SOFTWARE_PAGE || location.pathname === SOFTWARE_IDE_PAGE || location.pathname === SOFTWARE_PLC_PAGE) && (
                  <a href="https://arduino.github.io/arduino-cli/0.34/" className="sidebar-mobile__programming">
                    <span>CLI</span>
                    {getIcon('IconArrowRightJet')}
                  </a>
                )}
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
